import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";

// Deep import (NOT the "@pontimed/ui/globe" barrel) so this hook — which sits in
// the entry's static graph — doesn't drag svg-globe-map.tsx + react-simple-maps
// (~71KB) onto the first-paint critical path. The globe component stays lazy via
// the dynamic import() in country-picker-sheet.tsx (D-A4 / S2).
import { getCountryLevel, type CountryLevel } from "@pontimed/ui/globe/get-country-level";

/**
 * Shape returned by GET /api/countries (apps/api countries route).
 * Field set is a superset of what the picker needs — we keep the rest as
 * passthrough for future consumers.
 */
export interface CountryRow {
  code: string;
  name: Record<string, string>;
  flag: string;
  platformStatus: string;
  providers: number;
  facilities: number;
  claimed: number;
  providersWithSchedule: number;
  languages: string[];
  domain: string | null;
  waitlistEnabled: boolean;
  referralEnabled: boolean;
  foundingProviderEnabled: boolean;
  pppMultiplier: number;
  exchangeRate: number;
  currencySymbol: string;
}

/** Picker-shaped row: country code (UPPER) + computed CountryLevel for globe coloring. */
export interface PickerCountry {
  /** ISO 3166-1 alpha-2 UPPERCASE — globe expects uppercase */
  code: string;
  level: CountryLevel;
  /** Raw `platform_status` (`hidden | coming_soon | open`) — drives the
   *  selection gate: only `open` countries close the picker on click. */
  platformStatus: string;
  flag: string;
  /** LocalizedString JSON: { en: "Romania", ro: "România", ... } */
  name: Record<string, string>;
  providers: number;
  facilities: number;
  claimed: number;
  providersWithSchedule: number;
  /** Toggles waitlist signup form on coming_soon countries (per launch plan). */
  waitlistEnabled: boolean;
  /** Activates referral commissions; on coming_soon → user can earn after launch. */
  referralEnabled: boolean;
  foundingProviderEnabled: boolean;
  pppMultiplier: number;
  exchangeRate: number;
  currencySymbol: string;
}

/**
 * Fetch the full country list with computed CountryLevel. Cached for
 * 5 minutes — list rarely changes within a session.
 */
export function useCountriesQuery() {
  return useQuery<PickerCountry[]>({
    queryKey: ["countries", "all"],
    queryFn: async () => {
      const rows = await apiFetch.get<CountryRow[]>("/api/countries");
      return rows.map((row) => ({
        code: row.code.toUpperCase(),
        level: getCountryLevel({
          platformStatus: row.platformStatus,
          totalFacilities: row.facilities,
          totalProviders: row.providers,
          totalClaimed: row.claimed,
          providersWithSchedule: row.providersWithSchedule,
        }),
        platformStatus: row.platformStatus,
        flag: row.flag,
        name: row.name,
        providers: row.providers,
        facilities: row.facilities,
        claimed: row.claimed,
        providersWithSchedule: row.providersWithSchedule,
        waitlistEnabled: row.waitlistEnabled,
        referralEnabled: row.referralEnabled,
        foundingProviderEnabled: row.foundingProviderEnabled,
        pppMultiplier: row.pppMultiplier,
        exchangeRate: row.exchangeRate,
        currencySymbol: row.currencySymbol,
      }));
    },
    staleTime: 5 * 60_000,
  });
}
